import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import {
  api,
  buildAssociatedLoansQueryParams,
} from "./ProfessionalContacts.helper";
import StatusTag from "./StatusTag";
import useStyles from "./ProfessionalContacts.styles";

const PREVIEW_PAGE_SIZE = 5;

/** Loose, non-blocking status→tone mapping — AssociatedLoan.status isn't a contract enum (the
 * core Loan domain owns its vocabulary, see 00-overview.md §5), so this is a best-effort read of
 * a few likely values rather than an exhaustive/confirmed mapping. */
export function loanStatusTone(status) {
  if (!status) return "neutral";
  const value = status.toLowerCase();
  if (value === "distressed") return "danger";
  if (value === "live") return "valid";
  return "neutral";
}

/** Matches FirmRecord.jsx's existing PII-value formatting precedent (£ + en-GB grouping). */
export const formatMoney = (value) =>
  value || value === 0 ? `£${Number(value).toLocaleString("en-GB")}` : "-";

const AssociatedLoansPreview = ({ firmId, associatedLoanCount, onViewAll }) => {
  const classes = useStyles();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!associatedLoanCount) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listAssociatedLoans(
        firmId,
        buildAssociatedLoansQueryParams({ pageSize: PREVIEW_PAGE_SIZE })
      )
      .then((page) => {
        if (!cancelled) setItems(page?.items || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [firmId, associatedLoanCount]);

  return (
    <>
      <div className={classes.sectionHeading}>
        Associated loans
        {Boolean(associatedLoanCount) && (
          <span className={classes.countChip}>{associatedLoanCount}</span>
        )}
      </div>

      {!associatedLoanCount ? (
        <M_DataStateRenderer
          loading={false}
          error={null}
          isEmpty
          emptyMessage="No associated loans yet."
        >
          {null}
        </M_DataStateRenderer>
      ) : (
        <>
          {error && (
            <div className={classes.subtle}>
              <InfoIcon fontSize="inherit" /> Couldn&rsquo;t load a preview. Try &ldquo;View all&rdquo; below.
            </div>
          )}
          {!error &&
            (loading && items.length === 0 ? (
              <div className={classes.subtle}>Loading…</div>
            ) : (
              items.map((loan) => (
                <div
                  key={`${loan.loanId}-${loan.role}`}
                  className={clsx(classes.rowItem, classes.rowItemStatic)}
                >
                  <div className={classes.rowGrow}>
                    <div className={classes.cellStrong}>{loan.loanName}</div>
                    <div className={classes.subtle}>
                      {loan.borrowerName} · Ref {loan.loanNumber}
                    </div>
                  </div>
                  <div className={classes.rowMetaGroup}>
                    <span className={classes.rowMeta}>{loan.roleLabel}</span>
                    <span
                      className={clsx(classes.cellStrong, classes.cellNowrap)}
                    >
                      {formatMoney(loan.facilityLimit)}
                    </span>
                    <StatusTag tone={loanStatusTone(loan.status)}>
                      {loan.status || "-"}
                    </StatusTag>
                  </div>
                </div>
              ))
            ))}
          <button
            type="button"
            className={classes.linkAction}
            onClick={onViewAll}
          >
            View All {associatedLoanCount} Associated Loans →
          </button>
        </>
      )}
    </>
  );
};

AssociatedLoansPreview.propTypes = {
  firmId: PropTypes.string.isRequired,
  associatedLoanCount: PropTypes.number,
  onViewAll: PropTypes.func.isRequired,
};

export default AssociatedLoansPreview;
